import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWalletSafe } from "@/hooks/useWalletSafe";

export type WalletSessionStatus = "valid" | "expired" | "none" | "syncing" | "needs_2fa";

const ADMIN_WALLET = "ADJkonbgxndjbyL5XHfBLkVhEGiTUBChK9YMg8PtmPKa";

// Singleton state to prevent duplicate sync attempts across components
let globalSyncPromise: Promise<boolean> | null = null;
let globalSyncingWallet: string | null = null;
let global2FAVerified = false;

/**
 * Ensures a Supabase auth session exists for the currently connected wallet.
 * Uses a singleton pattern to prevent duplicate sync attempts.
 */
export function useWalletSupabaseSession() {
  const { connected, publicKey, signMessage } = useWalletSafe();
  const [status, setStatus] = useState<WalletSessionStatus>("none");
  const [needs2FA, setNeeds2FA] = useState(false);
  const syncAttempted = useRef(false);
  const signMessageRef = useRef(signMessage);

  // Keep signMessage ref updated without triggering re-renders
  useEffect(() => {
    signMessageRef.current = signMessage;
  }, [signMessage]);

  const walletAddress = publicKey?.toBase58() ?? "";
  const isAdminWallet = walletAddress === ADMIN_WALLET;

  // Function to mark 2FA as verified and continue auth
  const confirm2FA = useCallback(() => {
    global2FAVerified = true;
    setNeeds2FA(false);
    setStatus("syncing");
  }, []);

  useEffect(() => {
    if (!connected || !walletAddress) {
      setStatus("none");
      setNeeds2FA(false);
      syncAttempted.current = false;
      global2FAVerified = false;
      return;
    }

    let cancelled = false;

    const sync = async () => {
      try {
        console.log("[WalletSession] Starting sync for wallet:", walletAddress.slice(0, 8));
        
        // First check if we already have a valid session
        const { data: { session } } = await supabase.auth.getSession();
        const sessionWallet = session?.user?.user_metadata?.wallet_address as string | undefined;
        const expiresAt = session?.expires_at;

        console.log("[WalletSession] Current session:", {
          hasSession: !!session,
          sessionWallet: sessionWallet?.slice(0, 8),
          walletMatch: sessionWallet === walletAddress,
          expiresAt: expiresAt ? new Date(expiresAt * 1000).toISOString() : null,
          isValid: expiresAt && expiresAt * 1000 > Date.now(),
        });

        if (session && sessionWallet === walletAddress && expiresAt && expiresAt * 1000 > Date.now()) {
          console.log("[WalletSession] Session is valid, no sync needed");
          if (!cancelled) setStatus("valid");
          return;
        }

        // Admin wallet requires 2FA before continuing
        if (isAdminWallet && !global2FAVerified) {
          console.log("[WalletSession] Admin wallet detected, requiring 2FA");
          if (!cancelled) {
            setStatus("needs_2fa");
            setNeeds2FA(true);
          }
          return;
        }

        // Check if another component is already syncing this wallet
        if (globalSyncingWallet === walletAddress && globalSyncPromise) {
          if (!cancelled) setStatus("syncing");
          const success = await globalSyncPromise;
          if (!cancelled) setStatus(success ? "valid" : "expired");
          return;
        }

        // Prevent duplicate syncs from the same component
        if (syncAttempted.current) {
          if (!cancelled) setStatus("expired");
          return;
        }

        const currentSignMessage = signMessageRef.current;
        if (!currentSignMessage) {
          if (!cancelled) setStatus("expired");
          return;
        }

        syncAttempted.current = true;
        if (!cancelled) setStatus("syncing");

        // Create the sync promise
        globalSyncingWallet = walletAddress;
        globalSyncPromise = (async () => {
          try {
            console.log("[WalletSession] Starting signature-based auth flow...");
            const { requestAuthNonce, verifySignature } = await import("@/lib/walletConnect");
            
            console.log("[WalletSession] Requesting nonce...");
            const nonceData = await requestAuthNonce(walletAddress);
            if (!nonceData) throw new Error("Failed to get nonce");
            console.log("[WalletSession] Got nonce, requesting signature...");

            const encodedMessage = new TextEncoder().encode(nonceData.message);
            const signatureUint8 = await currentSignMessage(encodedMessage);
            console.log("[WalletSession] Got signature, verifying...");
            
            const bs58 = await import("bs58");
            const signature = bs58.default.encode(signatureUint8);

            const verifyResult = await verifySignature(walletAddress, signature, nonceData.message);
            console.log("[WalletSession] Verify result:", { success: verifyResult.success, error: verifyResult.error });
            
            if (!verifyResult.success || !verifyResult.session) {
              throw new Error(verifyResult.error || "Signature verification failed");
            }

            await supabase.auth.setSession({
              access_token: verifyResult.session.access_token,
              refresh_token: verifyResult.session.refresh_token,
            });

            console.log("[WalletSession] Session set successfully!");
            return true;
          } catch (e) {
            console.error("[WalletSession] Wallet→session sync failed:", e);
            return false;
          } finally {
            globalSyncingWallet = null;
            globalSyncPromise = null;
          }
        })();

        const success = await globalSyncPromise;
        if (!cancelled) setStatus(success ? "valid" : "expired");

      } catch (e) {
        console.error("Wallet→session sync error:", e);
        if (!cancelled) setStatus("expired");
      }
    };

    sync();

    return () => {
      cancelled = true;
    };
  }, [connected, walletAddress, isAdminWallet, needs2FA]);

  return {
    status,
    walletAddress,
    isAdminWallet,
    needs2FA,
    confirm2FA,
  };
}
